'use client';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, CreditCard, Bell, BarChart2, Shield, DollarSign, Zap, Users, RefreshCw, Globe, ChevronRight, Menu, X, FileText, Smartphone, Target, TrendingUp, Lock, Briefcase } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { getBrandingConfig } from '@/utils/branding';
import { cn } from '@/lib/utils';

export default function InvoiceBugMarketingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const brandingConfig = getBrandingConfig();

  return (
    <div className="flex flex-col min-h-screen">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Briefcase className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                {brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'}
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Features</a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Pricing</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Testimonials</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact</a>
            </div>
            
            <div className="hidden md:flex items-center space-x-4">
              <Link href="/login?redirect=/app">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  Sign In
                </Button>
              </Link>
              <Link href="/free-trial-signup">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
            
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </nav>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg">
              <div className="flex flex-col space-y-4 p-4">
                <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium">Features</a>
                <a href="#pricing" className="text-gray-700 hover:text-blue-600 font-medium">Pricing</a>
                <a href="#testimonials" className="text-gray-700 hover:text-blue-600 font-medium">Testimonials</a>
                <a href="#contact" className="text-gray-700 hover:text-blue-600 font-medium">Contact</a>
                <Link href="/login?redirect=/app">
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600">
                    Sign In
                  </Button>
                </Link>
                <Link href="/free-trial-signup">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate-200 [mask-image:radial-gradient(ellipse_at_center,white,transparent)] opacity-10"></div>
        
        {/* Floating elements for visual appeal */}
        <div className="absolute top-20 right-20 w-16 h-16 bg-blue-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 left-10 w-8 h-8 bg-indigo-300 rounded-full opacity-30 animate-bounce"></div>
        <div className="absolute bottom-32 right-40 w-12 h-12 bg-purple-200 rounded-full opacity-25"></div>
        
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Trust badge */}
              <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 border border-green-200 w-fit">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">Trusted by 10,000+ businesses</span>
              </div>
              
              <div className="space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Stop Chasing Payments.
                  <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    Get Paid Automatically
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  The only invoicing platform that guarantees you keep 100% of your revenue. Create professional invoices, accept payments instantly, and automate your entire billing process - all with zero platform fees on ACH payments.
                </p>
              </div>
              
              {/* Value proposition callout */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-r-xl p-6">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Save $3,000+ Per Year</h3>
                    <p className="text-gray-700 text-sm">The average business processing $100K annually saves over $3,000 in fees by switching to {brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'}'s ACH payment option. Your customers choose how to pay, you keep every dollar.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/free-trial-signup">
                  <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all text-lg px-8 py-4">
                    Start Free Trial - No Credit Card
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#demo">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-gray-300 hover:border-blue-600 hover:text-blue-600 text-lg px-8 py-4">
                    See How It Works
                  </Button>
                </a>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-semibold text-gray-900">Setup in 5 min</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-semibold text-gray-900">Next-day deposits</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-semibold text-gray-900">Cancel anytime</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              {/* Main dashboard image */}
              <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-white transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2070&auto=format&fit=crop"
                  alt="{brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'} Dashboard showing professional invoices and real-time analytics"
                  className="w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              {/* Floating success notification */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-green-200 max-w-xs animate-bounce">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Payment Received</p>
                    <p className="text-lg font-bold text-gray-900">$4,250.00</p>
                  </div>
                </div>
              </div>
              
              {/* Floating stats card */}
              <div className="absolute -top-6 -right-6 bg-blue-600 text-white rounded-xl shadow-lg p-4 max-w-xs">
                <div className="text-center">
                  <TrendingUp className="h-6 w-6 mx-auto mb-1" />
                  <p className="text-xs opacity-90">Faster Payments</p>
                  <p className="text-xl font-bold">73%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-blue-50/30 border-y">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Join Thousands of Businesses Getting Paid Faster
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From freelancers to enterprises, InvoiceBug helps businesses of all sizes streamline their billing and maximize their cash flow.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">10K+</p>
                <p className="text-gray-600 font-medium">Happy Businesses</p>
                <p className="text-xs text-gray-500 mt-1">Growing every day</p>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-4xl md:text-5xl font-bold text-green-600 mb-2">$50M+</p>
                <p className="text-gray-600 font-medium">Processed Monthly</p>
                <p className="text-xs text-gray-500 mt-1">Secure & reliable</p>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">99.9%</p>
                <p className="text-gray-600 font-medium">Uptime</p>
                <p className="text-xs text-gray-500 mt-1">Always available</p>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">24/7</p>
                <p className="text-gray-600 font-medium">Support</p>
                <p className="text-xs text-gray-500 mt-1">When you need us</p>
              </div>
            </div>
          </div>
          
          {/* Security badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="h-5 w-5" />
              <span className="text-sm font-medium">PCI Level 1 Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Lock className="h-5 w-5" />
              <span className="text-sm font-medium">Bank-Level Security</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">SOC 2 Certified</span>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="bg-blue-50 text-blue-800 px-4 py-2 rounded-full text-sm font-medium w-fit mx-auto mb-6">
              ⚡ 10x Faster Than Traditional Invoicing
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Stop Losing Money on Payment Processing Fees
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              While other platforms take 3-5% of every transaction, InvoiceBug lets you keep 100% of your revenue. 
              Here's how we're revolutionizing business payments:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Professional Invoicing */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-blue-100 hover:border-blue-200 group">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Professional Invoicing</h3>
                <Badge className="bg-green-100 text-green-800 text-xs">Most Popular</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Turn 5-hour billing tasks into 5-minute workflows.</span> Create stunning, branded invoices that get paid 73% faster than traditional methods.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Custom branding</strong> - Your logo, colors, and style</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Auto-numbering</strong> - Never manually track invoices again</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Global ready</strong> - Multi-currency & tax support</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-xs text-blue-700 font-medium">💡 Average time savings: 4+ hours per week</p>
              </div>
            </div>

            {/* Integrated Payment Processing */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-green-100 hover:border-green-200 group">
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <CreditCard className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Zero-Fee ACH Payments</h3>
                <Badge className="bg-yellow-100 text-yellow-800 text-xs">💰 Money Saver</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Keep 100% of every payment.</span> Unlike competitors who charge 2.9-3.5% per transaction, we offer zero-fee ACH payments so you never lose money to processing fees.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Next-day deposits</strong> - Get your money faster</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Zero ACH fees</strong> - No more 3% transaction costs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Bank-level security</strong> - PCI Level 1 compliant</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-xs text-green-700 font-medium">💡 Average savings: $3,000+ per year</p>
              </div>
            </div>

            {/* Recurring Billing */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-purple-100 hover:border-purple-200 group">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <RefreshCw className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Autopilot Revenue</h3>
                <Badge className="bg-purple-100 text-purple-800 text-xs">🚀 Automate</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Set it once, earn forever.</span> Automate subscription billing, memberships, and recurring services. Never chase another payment again.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Smart scheduling</strong> - Weekly, monthly, or custom intervals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Auto-retry failed payments</strong> - Recover 67% of failed charges</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Customer portal</strong> - Let clients manage their own subscriptions</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-purple-200">
                <p className="text-xs text-purple-700 font-medium">💡 Increase monthly recurring revenue by 35%</p>
              </div>
            </div>

            {/* Virtual Terminal */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-orange-100 hover:border-orange-200 group">
              <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Smartphone className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Mobile Payment Terminal</h3>
                <Badge className="bg-orange-100 text-orange-800 text-xs">📱 Mobile</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Take payments anywhere, anytime.</span> Perfect for phone orders, in-person sales, or emergency payments. Your mobile payment processing station.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Instant processing</strong> - Charge cards in seconds</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Secure card vault</strong> - Save for future one-click payments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Mobile-first design</strong> - Works perfectly on any device</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-orange-200">
                <p className="text-xs text-orange-700 font-medium">💡 Perfect for phone orders and field sales</p>
              </div>
            </div>

            {/* Business Intelligence */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-cyan-100 hover:border-cyan-200 group">
              <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BarChart2 className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Smart Business Insights</h3>
                <Badge className="bg-cyan-100 text-cyan-800 text-xs">📊 Analytics</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Know exactly where your money comes from.</span> Real-time dashboards show revenue trends, customer behavior, and cash flow predictions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Revenue tracking</strong> - See daily, weekly, monthly trends</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Customer insights</strong> - Identify your best clients</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Cash flow forecasts</strong> - Predict future revenue</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-cyan-200">
                <p className="text-xs text-cyan-700 font-medium">💡 Make data-driven decisions with confidence</p>
              </div>
            </div>

            {/* Embeddable Checkout */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300 border border-indigo-100 hover:border-indigo-200 group">
              <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Globe className="h-7 w-7 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold">Website Payment Forms</h3>
                <Badge className="bg-indigo-100 text-indigo-800 text-xs">🌐 Embed</Badge>
              </div>
              <p className="text-gray-600 mb-4">
                <span className="font-semibold text-gray-900">Turn any website into a payment processor.</span> Custom payment forms and donation pages that match your brand perfectly.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Custom branding</strong> - Match your website design</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Donation forms</strong> - Perfect for non-profits</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700"><strong>Mobile-responsive</strong> - Works on all devices</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-indigo-200">
                <p className="text-xs text-indigo-700 font-medium">💡 Increase online conversion rates by 45%</p>
              </div>
            </div>
          </div>
          
          {/* CTA after features */}
          <div 
            className={cn(
              "text-center mt-16 rounded-2xl p-8 md:p-12 text-white",
              "bg-gradient-to-r from-blue-600 to-indigo-600"
            )}
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Keep 100% of Your Revenue?
            </h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses who switched to {brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'} and never looked back. Start your free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/free-trial-signup">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8">
                  Start Free 14-Day Trial
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Talk to Sales Team
              </Button>
            </div>
            <p className="text-blue-200 text-sm mt-4">
              ✓ No credit card required  ✓ Setup in 5 minutes  ✓ Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Payment Processing Highlight */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Smart Payment Options That Save You Money
              </h2>
              <p className="text-xl text-blue-100">
                Your business shouldn't have to absorb payment processing costs. With InvoiceBug's flexible platform, you can offer customers choices that work for everyone.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <DollarSign className="h-10 w-10 text-blue-200 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Flexible Payment Options</h3>
                  <p className="text-blue-100">Accept both ACH bank transfers and credit card payments, giving customers the flexibility to choose their preferred payment method</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Zap className="h-10 w-10 text-blue-200 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Next-Day Deposits</h3>
                  <p className="text-blue-100">Get your money faster with deposits as soon as the next business day</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Shield className="h-10 w-10 text-blue-200 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Secure Platform</h3>
                  <p className="text-blue-100">InvoiceBug handles all payment collection with bank-level security</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Users className="h-10 w-10 text-blue-200 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Customer Choice</h3>
                  <p className="text-blue-100">Empower customers to select their preferred payment method</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070&auto=format&fit=crop"
                alt="Payment Processing"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Received</p>
                    <p className="text-lg font-bold text-gray-900">$3,250.00</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions - Enhanced Version */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/40 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <div className="bg-blue-50 text-blue-800 px-4 py-2 rounded-full text-sm font-medium w-fit mx-auto mb-6">
              🏆 Trusted Across Industries
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Built for Every Business,
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Designed for Success
              </span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              From solo entrepreneurs to Fortune 500 companies, InvoiceBug adapts to your unique industry needs with specialized features that maximize revenue and minimize hassle.
            </p>
          </div>
          
          <div className="space-y-24">
            {/* Freelancers Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">Solo Professionals</Badge>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">
                  Freelancers & Consultants
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">Stop chasing payments and focus on what you do best.</span> Our streamlined invoicing gets you paid 73% faster than traditional methods, with zero-fee ACH payments keeping 100% of your earnings in your pocket.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Professional branded invoices</p>
                    <p className="text-xs text-gray-600">Build trust & get paid faster</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Automatic payment reminders</p>
                    <p className="text-xs text-gray-600">Never chase clients again</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Time tracking integration</p>
                    <p className="text-xs text-gray-600">Bill accurately for every hour</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Expense management</p>
                    <p className="text-xs text-gray-600">Track costs & maximize profit</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                    <h4 className="font-bold text-gray-900">Average Freelancer Saves $1,800/year</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    "I was losing $150/month to payment processing fees. InvoiceBug's ACH option eliminated that completely!" - Maria K., Graphic Designer
                  </p>
                </div>
                
                <Link href="/free-trial-signup" className="inline-block">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white">
                    Start Free Trial for Freelancers
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              
              <div className="relative order-1 lg:order-2">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop"
                    alt="Freelancer working on laptop with coffee"
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">This month's earnings</p>
                          <p className="text-2xl font-bold text-gray-900">$8,450</p>
                        </div>
                        <div className="text-green-600">
                          <TrendingUp className="h-6 w-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating success indicator */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white rounded-full p-3 shadow-lg animate-pulse">
                  <CheckCircle className="h-6 w-6" />
                </div>
              </div>
            </div>

            {/* Agencies Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop"
                    alt="Agency team collaborating in modern office"
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Team invoices sent</p>
                          <p className="text-2xl font-bold text-gray-900">47 this week</p>
                        </div>
                        <div className="text-blue-600">
                          <Users className="h-6 w-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating team indicator */}
                <div className="absolute -top-4 -left-4 bg-blue-500 text-white rounded-full p-3 shadow-lg animate-bounce">
                  <Users className="h-6 w-6" />
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">Growing Teams</Badge>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">
                  Agencies & Creative Studios
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">Scale your billing operations without scaling your headaches.</span> Multi-user access, client portals, and automated workflows let your team focus on delivering exceptional work while invoices handle themselves.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Multi-user team access</p>
                    <p className="text-xs text-gray-600">Everyone stays in sync</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Project-based billing</p>
                    <p className="text-xs text-gray-600">Track multiple client projects</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Client approval workflows</p>
                    <p className="text-xs text-gray-600">Streamline approvals</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Advanced reporting</p>
                    <p className="text-xs text-gray-600">Track team performance</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart2 className="h-6 w-6 text-blue-600" />
                    <h4 className="font-bold text-gray-900">35% Faster Project Completion</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    "Our 12-person team now processes invoices in minutes, not hours. Game changer!" - David R., Creative Director
                  </p>
                </div>
                
                <Link href="/free-trial-signup" className="inline-block">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
                    Start Free Trial for Agencies
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Non-Profits Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <Badge className="bg-green-100 text-green-800">Impact Organizations</Badge>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">
                  Non-Profits & Charities
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">Keep 100% of every donation where it belongs - funding your mission.</span> Specialized donation forms, recurring giving programs, and donor management tools that maximize impact while minimizing administrative overhead.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-green-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Recurring donation setup</p>
                    <p className="text-xs text-gray-600">Build sustainable funding</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-green-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Donor management system</p>
                    <p className="text-xs text-gray-600">Track relationships & impact</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-green-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Tax receipt automation</p>
                    <p className="text-xs text-gray-600">Instant donor receipts</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-green-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Campaign tracking</p>
                    <p className="text-xs text-gray-600">Measure fundraising success</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Bell className="h-6 w-6 text-green-600" />
                    <h4 className="font-bold text-gray-900">35% Increase in Recurring Donations</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    "Monthly giving grew 35% in our first quarter. The simple donation forms work perfectly!" - Sarah T., Development Director
                  </p>
                </div>
                
                <Link href="/free-trial-signup" className="inline-block">
                  <Button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white">
                    Start Free Trial for Non-Profits
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              
              <div className="relative order-1 lg:order-2">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=2073&auto=format&fit=crop"
                    alt="Volunteers working together at non-profit organization"
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Monthly donations</p>
                          <p className="text-2xl font-bold text-gray-900">$24,580</p>
                        </div>
                        <div className="text-green-600">
                          <TrendingUp className="h-6 w-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating heart indicator */}
                <div className="absolute -top-4 -right-4 bg-red-500 text-white rounded-full p-3 shadow-lg animate-pulse">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Enterprises Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
                    alt="Modern corporate office with skyscrapers"
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/40 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Enterprise volume</p>
                          <p className="text-2xl font-bold text-gray-900">$2.4M this quarter</p>
                        </div>
                        <div className="text-indigo-600">
                          <BarChart2 className="h-6 w-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating enterprise indicator */}
                <div className="absolute -top-4 -left-4 bg-indigo-500 text-white rounded-full p-3 shadow-lg animate-bounce">
                  <TrendingUp className="h-6 w-6" />
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-800">Large Organizations</Badge>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">
                  Enterprises & Corporations
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">Scale billing operations that handle millions in revenue.</span> Enterprise-grade security, custom integrations, dedicated support, and volume pricing that grows with your business while maintaining the simplicity your teams love.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-indigo-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Custom API integrations</p>
                    <p className="text-xs text-gray-600">Connect existing systems</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-indigo-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Advanced user permissions</p>
                    <p className="text-xs text-gray-600">Enterprise security controls</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-indigo-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Dedicated account manager</p>
                    <p className="text-xs text-gray-600">White-glove support</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-indigo-100">
                    <CheckCircle className="h-5 w-5 text-green-500 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Volume pricing discounts</p>
                    <p className="text-xs text-gray-600">Scale with your business</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="h-6 w-6 text-indigo-600" />
                    <h4 className="font-bold text-gray-900">Save $50K+ Annually on Processing Fees</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    "We process $2M monthly. InvoiceBug's ACH options saved us over $50K in fees last year alone." - Jennifer L., CFO
                  </p>
                </div>
                
                <Link href="/free-trial-signup" className="inline-block">
                  <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white">
                    Start Enterprise Trial
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Industry CTA */}
          <div className="text-center mt-20 bg-gradient-to-r from-gray-900 to-blue-900 rounded-3xl p-12 text-white relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-10 left-10 w-20 h-20 bg-blue-400 rounded-full opacity-10 animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-16 h-16 bg-purple-400 rounded-full opacity-10 animate-bounce"></div>
            <div className="absolute top-1/2 left-1/4 w-8 h-8 bg-cyan-400 rounded-full opacity-20"></div>
            
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Industry?
              </h3>
              <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
                Join thousands of businesses across every industry who've made the smart switch to InvoiceBug. See why we're the #1 choice for businesses that want to keep 100% of their revenue.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link href="/free-trial-signup">
                  <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-4 text-lg">
                    Start Your Free 14-Day Trial
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg">
                  Schedule a Demo
                </Button>
              </div>
              <p className="text-gray-300 text-sm mt-6">
                ✓ No credit card required  ✓ Industry-specific setup  ✓ Dedicated onboarding support
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-medium w-fit mx-auto mb-6">
              ⭐ 4.9/5 Stars from 2,451 Reviews
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real Stories from Real Business Owners
            </h2>
            <p className="text-xl text-gray-600">
              Don't take our word for it. Here's how InvoiceBug transformed these businesses and helped them keep more of their hard-earned revenue.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                ))}
                <Badge className="ml-2 bg-blue-100 text-blue-800 text-xs">Verified Customer</Badge>
              </div>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                "We were losing <strong>$2,400 annually</strong> to payment processing fees. InvoiceBug's ACH option cut that to zero, and our clients love the faster payment process."
              </p>
              <div className="bg-green-50 p-3 rounded-lg mb-6">
                <p className="text-sm text-green-800 font-medium">💰 Annual Savings: $2,400</p>
              </div>
              <div className="flex items-center">
                <img
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
                  alt="Sarah Thompson"
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <p className="font-semibold text-gray-900">Sarah Thompson</p>
                  <p className="text-gray-500 text-sm">CEO, Digital Agency Plus</p>
                  <p className="text-blue-600 text-xs">$80K annual revenue</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                ))}
                <Badge className="ml-2 bg-purple-100 text-purple-800 text-xs">Non-Profit</Badge>
              </div>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                "The recurring donation feature increased our monthly giving by <strong>35%</strong>. Donors can set up automatic payments and we keep 100% of donations."
              </p>
              <div className="bg-purple-50 p-3 rounded-lg mb-6">
                <p className="text-sm text-purple-800 font-medium">📈 Donation Growth: +35%</p>
              </div>
              <div className="flex items-center">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
                  alt="Michael Chen"
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <p className="font-semibold text-gray-900">Michael Chen</p>
                  <p className="text-gray-500 text-sm">Director, Hope Foundation</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                ))}
                <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Freelancer</Badge>
              </div>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                "Went from waiting <strong>45+ days</strong> for payments to getting paid in 3-5 days. The professional invoices and payment reminders transformed my cash flow completely."
              </p>
              <div className="bg-blue-50 p-3 rounded-lg mb-6">
                <p className="text-sm text-blue-800 font-medium">⚡ Payment Time: 45 days → 3 days</p>
              </div>
              <div className="flex items-center">
                <img
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
                  alt="Jessica Martinez"
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <p className="font-semibold text-gray-900">Jessica Martinez</p>
                  <p className="text-gray-500 text-sm">Freelance Web Designer</p>
                  <p className="text-green-600 text-xs">$60K annual revenue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that fits your business. All plans include payment processing and core features.
            </p>
          </div>
          
          <div className="text-center mb-16">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-2xl mx-auto">
              <p className="text-lg font-semibold text-gray-900 mb-2">
                🎉 Pricing Coming Soon!
              </p>
              <p className="text-gray-700">
                We're finalizing our pricing plans to ensure the best value for your business. Sign up for a free trial to get early access and special launch pricing.
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <Link href="/free-trial-signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                Start Your Free 14-Day Trial
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <p className="text-gray-600 mt-4">No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070&auto=format&fit=crop"
                alt="Security Features"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Bank-Level Security You Can Trust
              </h2>
              <p className="text-xl text-gray-600">
                Your data and your customers' payment information are protected with industry-leading security measures.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">PCI DSS Level 1 Compliant</h3>
                    <p className="text-gray-600">The highest level of payment security certification</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">End-to-End Encryption</h3>
                    <p className="text-gray-600">All data is encrypted in transit and at rest</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">99.9% Uptime SLA</h3>
                    <p className="text-gray-600">Reliable service you can count on 24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Streamline Your Invoicing?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses getting paid faster with {brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'}. Start your free trial today and see the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/free-trial-signup">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Start Free 14-Day Trial
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Talk to Sales
              </Button>
            </a>
          </div>
          <p className="text-blue-100 mt-6">No credit card required • Setup in minutes • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Briefcase className="h-8 w-8 text-blue-400" />
                <span className="text-2xl font-bold">{brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'}</span>
              </div>
              <p className="text-gray-400 mb-4">
                The smart invoicing platform that helps businesses get paid faster.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">API Docs</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Release Notes</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Careers</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Partners</a></li>
              </ul>
            </div>
            
            <div id="contact">
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Help Center</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Community</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Status</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Security</a></li>
                <li><a href="mailto:support@invoicebug.com" className="text-gray-400 hover:text-white">support@invoicebug.com</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <p className="text-gray-400 text-sm">
                  © {new Date().getFullYear()} {brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug'}. All rights reserved.
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {brandingConfig.isInvoicerGG ? 'Invoicer.gg is a product of Monarch Labs, LLC' : 'InvoiceBug is a product of Wiki Payit, LLC'}
                </p>
              </div>
              <div className="flex space-x-6">
                <a href="#" className="text-gray-400 hover:text-white text-sm">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm">Terms of Service</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm">Cookie Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
